import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSave,
  faUndo,
  faUser,
  faEnvelope,
  faPhone,
  faIdCard,
  faCalendar,
  faSignInAlt,
  faCopy,
  faCheck,
  faShieldAlt,
  faUserCircle,
  faKey,
} from '@fortawesome/free-solid-svg-icons'
import { useAuthContext } from '../contexts'
import { LoadingSpinner } from '../shared/components/ui'
import { supabase } from '../lib/supabase'
import { profileApi } from '../services/profileApi'
import { ChangePasswordModal } from './ChangePasswordModal'

interface ProfileFormData {
  firstName: string
  lastName: string
  mobileNumber: string
  description: string
}

export function ProfileSettings() {
  const { email, signOut } = useAuthContext()
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    description: '',
  })
  const [savedData, setSavedData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    mobileNumber: '',
    description: '',
  })
  const [hasChanges, setHasChanges] = useState(false)
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [userId, setUserId] = useState<string>('')
  const [userRole, setUserRole] = useState<string>('customer')
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const [lastLogin, setLastLogin] = useState<Date | null>(null)
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)

  useEffect(() => {
    const loadUserProfile = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const {
          data: { user },
          error: userError,
        } = await supabase.auth.getUser()

        if (userError || !user) {
          toast.error('Session invalid. Please log in again.')
          await signOut()
          return
        }

        setUserId(user.id)
        setIsEmailVerified(!!user.email_confirmed_at)
        setLastLogin(user.last_sign_in_at ? new Date(user.last_sign_in_at) : null)

        const appMetadata = user.app_metadata || {}
        const role = appMetadata.role || 'customer'
        setUserRole(role)

        const profile = await profileApi.getProfile()
        const profileData = {
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          mobileNumber: profile.mobileNumber || '',
          description: profile.description || '',
        }
        setFormData(profileData)
        setSavedData(profileData)
      } catch (err: any) {
        console.error('Error loading user profile:', err)
        setError(err.message || 'Failed to load profile data')
      } finally {
        setIsLoading(false)
      }
    }

    loadUserProfile()
  }, [signOut])

  useEffect(() => {
    if (!isLoading) {
      const changed =
        formData.firstName !== savedData.firstName ||
        formData.lastName !== savedData.lastName ||
        formData.mobileNumber !== savedData.mobileNumber ||
        formData.description !== savedData.description
      setHasChanges(changed)
    }
  }, [formData, savedData, isLoading])

  const handleInputChange = (field: keyof ProfileFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setError(null)

    try {
      await profileApi.updateProfile({
        firstName: formData.firstName || undefined,
        lastName: formData.lastName || undefined,
        mobileNumber: formData.mobileNumber || undefined,
        description: formData.description || undefined,
      })

      setSavedData({ ...formData })
      toast.success('Profile updated successfully!', { duration: 15000 })
    } catch (err: any) {
      console.error('Failed to update profile:', err)
      setError(err.message || 'Failed to update profile')
      toast.error('Failed to update profile: ' + (err.message || 'Unknown error'), { duration: 30000 })
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setFormData({ ...savedData })
  }

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const getInitials = () => {
    if (formData.firstName || formData.lastName) {
      const first = formData.firstName?.charAt(0)?.toUpperCase() || ''
      const last = formData.lastName?.charAt(0)?.toUpperCase() || ''
      return first + last
    }
    return email?.charAt(0)?.toUpperCase() || 'U'
  }

  const getDisplayName = () => {
    const firstName = formData.firstName?.trim()
    const lastName = formData.lastName?.trim()
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }
    if (firstName) {
      return firstName
    }
    if (lastName) {
      return lastName
    }
    return email || 'User'
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error && !userId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-red-800 mb-2">Profile Load Error</h3>
        <p className="text-red-700 mb-4">{error || 'Failed to load profile data'}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-20 h-20 min-w-[5rem] min-h-[5rem] aspect-square flex-shrink-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-semibold shadow-lg">
              {getInitials()}
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-semibold text-gray-900 truncate">{getDisplayName()}</h3>
              <p className="text-sm text-gray-600 mt-1 truncate">{email}</p>
              <div className="flex flex-wrap items-center mt-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                <span className="flex items-center">
                  <FontAwesomeIcon icon={faSignInAlt} className="mr-1 h-3 w-3" />
                  Last login: {lastLogin ? lastLogin.toLocaleDateString() : 'N/A'}
                </span>
                <span className="flex items-center">
                  <FontAwesomeIcon icon={faCalendar} className="mr-1 h-3 w-3" />
                  Email verified: {isEmailVerified ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 flex-shrink-0">
            {hasChanges && (
              <button
                onClick={handleReset}
                disabled={isSaving}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faUndo} className="mr-2 h-4 w-4" />
                Reset
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FontAwesomeIcon icon={faSave} className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faShieldAlt} className="mr-2 h-4 w-4 text-gray-400" />
              Account Information
            </h4>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email Address
                </label>
                <div className="mt-1 flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faEnvelope} className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{email || 'Not set'}</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">Primary email for notifications</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</label>
                <div className="mt-1 flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center space-x-2 overflow-hidden">
                    <FontAwesomeIcon icon={faIdCard} className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <span className="text-xs font-mono text-gray-700 truncate">{userId}</span>
                  </div>
                  <button
                    onClick={() => handleCopy(userId, 'userId')}
                    className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                    title="Copy User ID"
                  >
                    <FontAwesomeIcon icon={copiedField === 'userId' ? faCheck : faCopy} className="h-3 w-3" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider">User Role</label>
                <div className="mt-1 flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
                  <div className="flex items-center space-x-2">
                    <FontAwesomeIcon icon={faUserCircle} className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-700">{userRole}</span>
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">Your account role</p>
              </div>

              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowChangePasswordModal(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  <FontAwesomeIcon icon={faKey} className="mr-2 h-4 w-4" />
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <FontAwesomeIcon icon={faUser} className="mr-2 h-4 w-4 text-gray-400" />
              Personal Information
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your first name"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter your last name"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
                <div className="relative">
                  <input
                    type="tel"
                    value={formData.mobileNumber}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/\D/g, '')
                      if (cleaned.length <= 10) {
                        handleInputChange('mobileNumber', cleaned)
                      }
                    }}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="(555) 123-4567"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faPhone} className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
                <p className="mt-1 text-xs text-gray-500">10-digit US phone number</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Tell us a bit about yourself or add any notes..."
                  maxLength={500}
                />
                <p className="mt-1 text-xs text-gray-500">{formData.description.length}/500 characters</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
          <FontAwesomeIcon icon={faShieldAlt} className="mr-2 h-4 w-4 text-gray-400" />
          Account Status
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Role</p>
              <p className="text-xs text-gray-500">Account permissions</p>
            </div>
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                userRole === 'admin' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'
              }`}
            >
              {userRole}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Email Status</p>
              <p className="text-xs text-gray-500">Verification status</p>
            </div>
            <div
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isEmailVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {isEmailVerified ? 'Verified' : 'Pending'}
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-900">Last Login</p>
              <p className="text-xs text-gray-500">Most recent access</p>
            </div>
            <div className="text-sm text-gray-700">{lastLogin ? lastLogin.toLocaleDateString() : 'Never'}</div>
          </div>
        </div>
      </div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
        email={email || ''}
      />
    </div>
  )
}
